/**
 * Validation for the booking form's contact step.
 *
 * Kept apart from the component so it can be unit-tested: `pnpm test` runs
 * tests/booking-validation.test.mjs against it. The previous inline checks
 * accepted "aaaaaa" as a phone number and any non-empty string as a name.
 *
 * Each validator returns a Spanish error message, or null when the value is
 * acceptable.
 */

export interface ContactData {
  name: string;
  email: string;
  phone: string;
}

export type ContactErrors = Partial<Record<keyof ContactData, string>>;

export const validateName = (value: string): string | null => {
  const name = value.trim();
  if (name.length < 3 || !/\p{L}/u.test(name)) {
    return "Escribe tu nombre completo (al menos 3 letras).";
  }
  return null;
};

export const validateEmail = (value: string): string | null => {
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value.trim())) {
    return "Revisa el email: debe tener la forma nombre@dominio.com.";
  }
  return null;
};

export const validatePhone = (value: string): string | null => {
  // Separators people actually type; anything left over that is not a digit
  // (or a leading +) means this is not a phone number.
  const compact = value.replace(/[\s.\-()]/g, "");
  if (!/^\+?\d+$/.test(compact)) {
    return "El teléfono solo puede llevar números, y el prefijo + al principio.";
  }
  const digits = compact.replace(/\D/g, "");
  if (digits.length < 9 || digits.length > 15) {
    return "Revisa el teléfono: son 9 dígitos, o con prefijo internacional (+34…).";
  }
  return null;
};

/** Runs every validator and returns only the fields that failed. */
export const validateContactData = (data: ContactData): ContactErrors => {
  const errors: ContactErrors = {};
  const name = validateName(data.name);
  const email = validateEmail(data.email);
  const phone = validatePhone(data.phone);
  if (name) errors.name = name;
  if (email) errors.email = email;
  if (phone) errors.phone = phone;
  return errors;
};
